// Fatura reprovada no MeuFrete (PO1-3222): a transportadora vê os CT-e em
// questão e pode Contestar (texto obrigatório + anexos) ou Cancelar a fatura
// (confirmação na própria tela, sem window.confirm). Após sucesso, `onConcluido`
// recarrega a lista.
import React, { useEffect, useState } from "react";
import { Button, FormGroup, Input, Label, Modal, Spinner } from "reactstrap";

import SectionCard from "components/SectionCard";
import { AUTOPILOT_COLORS } from "components/autopilot/styles";
import { cancelarFatura, contestarFatura, ctesReprovados, mensagemErro } from "services/meuFreteApi";
import { fmtBRL, OBSERVACAO_MAX } from "views/Empresa/ConferenciaFatura/formatters";
import { rotuloDocumento } from "utils/modeloDocumento";
import AnexosCampo from "views/Empresa/ConferenciaFatura/AnexosCampo";

const mutedStyle = { color: AUTOPILOT_COLORS.muted, fontSize: "0.78rem" };
const erroStyle = { color: "var(--everlog-status-danger)", fontSize: "0.82rem", marginTop: 6 };

// Mensagem de validação da contestação, ou null.
export function validarContestacao(texto) {
  const t = String(texto || "").trim();
  if (!t) return "Explique por que a fatura está correta (obrigatório).";
  if (t.length > OBSERVACAO_MAX) return `Máximo de ${OBSERVACAO_MAX} caracteres.`;
  return null;
}

function ContestarModal({ isOpen, fatura, onClose, onConcluido }) {
  const [texto, setTexto] = useState("");
  const [arquivos, setArquivos] = useState([]);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setTexto("");
      setArquivos([]);
      setErro(null);
    }
  }, [isOpen]);

  const confirmar = async () => {
    const msg = validarContestacao(texto);
    if (msg) {
      setErro(msg);
      return;
    }
    setSalvando(true);
    setErro(null);
    try {
      await contestarFatura(fatura.id, { texto, arquivos });
      onClose();
      onConcluido("Contestação enviada. A fatura voltou para a auditoria do embarcador.");
    } catch (err) {
      setErro(mensagemErro(err, "Não foi possível enviar a contestação."));
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose} fade={false} modalClassName="modal-align-top">
      <div className="meufrete-contestar" style={{ padding: "1.25rem", background: AUTOPILOT_COLORS.surface, color: AUTOPILOT_COLORS.text }}>
        <div style={{ ...mutedStyle, textTransform: "uppercase", letterSpacing: "0.08em" }}>Minhas faturas</div>
        <h5 style={{ color: AUTOPILOT_COLORS.title, margin: "0.2rem 0 0.75rem" }}>Contestar fatura {fatura && fatura.numero}</h5>
        <FormGroup>
          <Label>Justificativa *</Label>
          <Input
            type="textarea"
            name="texto"
            rows={4}
            value={texto}
            maxLength={OBSERVACAO_MAX}
            placeholder="Explique por que a fatura está correta."
            onChange={(e) => {
              const valor = e.target.value;
              setTexto(valor);
            }}
            disabled={salvando}
          />
        </FormGroup>
        <AnexosCampo arquivos={arquivos} onChange={setArquivos} onErro={setErro} disabled={salvando} />
        {erro && <div style={erroStyle} role="alert">{erro}</div>}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem", marginTop: "0.75rem" }}>
          <Button color="secondary" outline onClick={onClose} disabled={salvando}>Voltar</Button>
          <Button color="warning" onClick={confirmar} disabled={salvando} data-acao="confirmar-contestacao">
            {salvando ? <Spinner size="sm" /> : "Enviar contestação"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export default function AcoesReprovada({ fatura, onConcluido }) {
  const [ctes, setCtes] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [erroCtes, setErroCtes] = useState(null);
  const [contestando, setContestando] = useState(false);
  const [confirmarCancelar, setConfirmarCancelar] = useState(false);
  const [cancelando, setCancelando] = useState(false);
  const [erroCancelar, setErroCancelar] = useState(null);

  const idFatura = fatura && fatura.id;
  useEffect(() => {
    setConfirmarCancelar(false);
    setErroCancelar(null);
    if (!idFatura) return undefined;
    let cancelado = false;
    setCarregando(true);
    setErroCtes(null);
    ctesReprovados(idFatura)
      .then((lista) => {
        if (!cancelado) setCtes(lista);
      })
      .catch((err) => {
        if (!cancelado) {
          setCtes([]);
          setErroCtes(mensagemErro(err, "Não foi possível carregar os CT-e."));
        }
      })
      .finally(() => {
        if (!cancelado) setCarregando(false);
      });
    return () => {
      cancelado = true;
    };
  }, [idFatura]);

  const cancelar = async () => {
    setCancelando(true);
    setErroCancelar(null);
    try {
      await cancelarFatura(idFatura, {});
      setConfirmarCancelar(false);
      onConcluido("Fatura cancelada.");
    } catch (err) {
      setErroCancelar(mensagemErro(err, "Não foi possível cancelar a fatura."));
    } finally {
      setCancelando(false);
    }
  };

  if (!fatura) return null;

  return (
    <div className="meufrete-reprovada" style={{ marginTop: "0.75rem" }}>
      <SectionCard variant="field">
        <div style={{ ...mutedStyle, fontWeight: 700, marginBottom: 4 }}>Documentos em questão</div>
        {carregando ? (
          <div style={mutedStyle}><Spinner size="sm" /> Carregando…</div>
        ) : erroCtes ? (
          <div style={erroStyle} role="alert">{erroCtes}</div>
        ) : ctes.length === 0 ? (
          <div style={mutedStyle}>Nenhum documento apontado pelo embarcador.</div>
        ) : (
          <ul className="meufrete-ctes-reprovados" style={{ margin: 0, paddingLeft: "1.1rem" }}>
            {ctes.map((c) => (
              <li key={c.id} style={{ color: AUTOPILOT_COLORS.text, fontSize: "0.84rem" }}>
                {rotuloDocumento(c.modelo)} {c.numero}{c.serie !== undefined && c.serie !== null ? `/${c.serie}` : ""} · {fmtBRL(c.valorFrete)}
              </li>
            ))}
          </ul>
        )}
      </SectionCard>

      {!confirmarCancelar ? (
        <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.75rem", flexWrap: "wrap" }}>
          <Button size="sm" color="warning" data-acao="contestar" onClick={() => setContestando(true)}>
            Contestar
          </Button>
          <Button size="sm" color="danger" outline data-acao="cancelar" onClick={() => setConfirmarCancelar(true)}>
            Cancelar fatura
          </Button>
        </div>
      ) : (
        <div role="alertdialog" aria-label="Confirmar cancelamento" style={{ marginTop: "0.75rem", padding: "0.6rem 0.75rem", borderRadius: 8, border: "1px solid var(--everlog-status-danger-border)", background: "var(--everlog-status-danger-bg)", color: AUTOPILOT_COLORS.text, fontSize: "0.84rem" }}>
          <div>Cancelar a fatura {fatura.numero}? Ela sai da auditoria e os CT-e voltam a ficar disponíveis para uma nova fatura.</div>
          {erroCancelar && <div style={erroStyle} role="alert">{erroCancelar}</div>}
          <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.5rem" }}>
            <Button size="sm" color="secondary" outline onClick={() => setConfirmarCancelar(false)} disabled={cancelando}>
              Voltar
            </Button>
            <Button size="sm" color="danger" data-acao="confirmar-cancelamento" onClick={cancelar} disabled={cancelando}>
              {cancelando ? <Spinner size="sm" /> : "Sim, cancelar"}
            </Button>
          </div>
        </div>
      )}

      <ContestarModal
        isOpen={contestando}
        fatura={fatura}
        onClose={() => setContestando(false)}
        onConcluido={onConcluido}
      />
    </div>
  );
}
